/**
 * Typed fetch client for sagwa-box backend.
 *
 * Auth model: every request that needs a user identity carries
 * `Authorization: tma <initData>` where initData is the raw string from
 * `window.Telegram.WebApp.initData`. The server validates the HMAC signature
 * on every call — no sessions, no cookies.
 *
 * Base URL is configurable via Vite env `VITE_API_BASE_URL` (default `''`,
 * meaning same-origin). Set this to point at a remote API host during local
 * dev or when the Mini App is hosted on a CDN while the API lives elsewhere.
 */
import type {
  AuthInitResponse,
  DailyHistoryResponse,
  DailyLeaderboardResponse,
  DailyStartResponse,
  DailySubmitRequest,
  DailySubmitResponse,
  DailySubmitError,
  DailyTodayResponse,
  HallOfFameResponse,
  MeResponse,
  WeeklyLeaderboardResponse,
} from '@sagwa/types'

const BASE_URL: string =
  (typeof import.meta !== 'undefined' &&
    (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_API_BASE_URL) ||
  ''

/** Either /v1/daily/start success OR error envelope. */
export type DailyStartResult =
  | DailyStartResponse
  | {
      ok: false
      reason: 'NO_STAMINA' | 'GAME_IN_PROGRESS' | 'INVALID_DATE' | 'RATE_LIMITED'
    }

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown
  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

function getInitData(): string {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined
  return tg?.initData ?? ''
}

function authHeaders(): Record<string, string> {
  const initData = getInitData()
  if (!initData) return {}
  return { Authorization: `tma ${initData}` }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Skip Authorization header (e.g. for unauthed health checks). */
  skipAuth?: boolean
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET'
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.skipAuth ? {} : authHeaders()),
  }
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const url = `${BASE_URL}${path}`
  let res: Response
  try {
    res = await fetch(url, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    })
  } catch (err) {
    throw new ApiError(
      `Network error: ${err instanceof Error ? err.message : String(err)}`,
      0,
      null,
    )
  }

  const ct = res.headers.get('content-type') ?? ''
  const isJson = ct.includes('application/json')
  const parsed: unknown = isJson ? await res.json().catch(() => null) : await res.text()

  if (!res.ok) {
    // Intercept 503 with MAINTENANCE body → dispatch global event for App root
    if (
      res.status === 503 &&
      isJson &&
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed as Record<string, unknown>)['error'] === 'MAINTENANCE'
    ) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('app:maintenance'))
      }
    }
    throw new ApiError(
      `Request failed: ${method} ${path} → ${res.status}`,
      res.status,
      parsed,
    )
  }

  return parsed as T
}

export const apiClient = {
  authInit(): Promise<AuthInitResponse> {
    return request<AuthInitResponse>('/v1/auth/init', {
      method: 'POST',
      body: { initData: getInitData() },
      skipAuth: true,
    })
  },

  getMe(): Promise<MeResponse> {
    return request<MeResponse>('/v1/me')
  },

  /** W3W4 P2-a: delete the calling user. Cascades to attempts + leaderboard rows. */
  deleteMe(): Promise<{ ok: true }> {
    return request<{ ok: true }>('/v1/me', { method: 'DELETE' })
  },

  getDailyToday(): Promise<DailyTodayResponse> {
    return request<DailyTodayResponse>('/v1/daily/today')
  },

  /**
   * Start a daily attempt. Server consumes 1 stamina, returns nonce + seed.
   *
   * Atomic-409 recovery: on 409 the server returns the current stamina state
   * inside the JSON body so the caller can resync without an extra /me round
   * trip.
   */
  async dailyStart(): Promise<DailyStartResult> {
    try {
      return await request<DailyStartResponse>('/v1/daily/start', { method: 'POST' })
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = (err.body ?? {}) as { reason?: string }
        const reason = body.reason
        if (
          reason === 'NO_STAMINA' ||
          reason === 'GAME_IN_PROGRESS' ||
          reason === 'INVALID_DATE'
        ) {
          return { ok: false, reason }
        }
        return { ok: false, reason: 'NO_STAMINA' }
      }
      if (err instanceof ApiError && err.status === 429) {
        return { ok: false, reason: 'RATE_LIMITED' }
      }
      throw err
    }
  },

  async dailySubmit(
    req: DailySubmitRequest,
  ): Promise<DailySubmitResponse | DailySubmitError> {
    try {
      return await request<DailySubmitResponse>('/v1/daily/submit', {
        method: 'POST',
        body: req,
      })
    } catch (err) {
      if (err instanceof ApiError && (err.status === 400 || err.status === 409)) {
        const body = (err.body ?? {}) as DailySubmitError
        if (body && body.ok === false && typeof body.reason === 'string') {
          return body
        }
      }
      throw err
    }
  },

  getDailyLeaderboard(date: string): Promise<DailyLeaderboardResponse> {
    return request<DailyLeaderboardResponse>(
      `/v1/daily/leaderboard?date=${encodeURIComponent(date)}`,
    )
  },

  getWeeklyLeaderboard(week: string): Promise<WeeklyLeaderboardResponse> {
    return request<WeeklyLeaderboardResponse>(
      `/v1/weekly/leaderboard?week=${encodeURIComponent(week)}`,
    )
  },

  getHallOfFame(): Promise<HallOfFameResponse> {
    return request<HallOfFameResponse>('/v1/halloffame')
  },

  /**
   * Page of caller's validated daily-challenge attempts (W2 commit 10).
   * Server enforces max limit=50; default page size is 20.
   */
  getDailyHistory(
    opts: { offset?: number; limit?: number } = {},
  ): Promise<DailyHistoryResponse> {
    const offset = opts.offset ?? 0
    const limit = opts.limit ?? 20
    return request<DailyHistoryResponse>(
      `/v1/me/daily/history?offset=${offset}&limit=${limit}`,
    )
  },
}

export type ApiClient = typeof apiClient
